import {isNumber} from "../utils/utils";
import {Capability} from './capability';
import {Platform, platformFromObject, platformFromString} from '@webmate-web-core/shared/types/platform';
import * as _ from 'lodash';

export type BrowserCapabilityName = string;

/** Represents browser in the UI including information about its representation. */
export class Browser {
  constructor(public readonly browserType: string, public readonly version: string, public platform: Platform, public readonly properties: any, public readonly model: string | undefined, public capabilities?: Record<BrowserCapabilityName, any>) {};

  /** custom equals method, named differently to avoid confusion*/
  equalsOtherBrowser(b: Browser): boolean {
    return !!b && this.browserType.toLowerCase() === b.browserType.toLowerCase() && this.version === b.version && JSON.stringify(this.platform) === JSON.stringify(b.platform) && _.isEqual(this.capabilities, b.capabilities) && this.model == b.model;
  }

  isSupportedByCapabilities(capabilities: Capability[]): boolean {
      return (capabilities.find(cap => cap.platform === this.platform.platformType+"_"+this.platform.platformVersion+"_"+this.platform.platformArchitecture && cap.browserType === this.browserType && cap.version == this.version && (!cap.model || cap.model === this.model)) !== undefined);
  }

  getFullName(): string {
    return this.browserType + " " + this.version + " " + this.platform.getFullName();
  }

  getShortName(): string {
    return this.shortBrowserType() + " " + this.version + " - " + this.platform.getShortName();
  }

  shortBrowserType(): string {
    switch(this.browserType) {
      case 'FIREFOX':
        return 'FF';
      case 'CHROME':
        return 'GC';
      default:
        return this.browserType;
    }
  }

  isMobile() {
      return this.platform.getFullName().toLowerCase().indexOf("android") !== -1 || this.platform.getFullName().toLowerCase().indexOf("ios") !== -1;
  }

  isActualBrowser() {
      return this.browserType.toLowerCase() !== 'appium'
  }

  /** Helper function to remove the properties bag from the browser, as it contains infos that are not always required or even harmful*/
  withoutProperties(): Browser {
      return new Browser(this.browserType, this.version, this.platform, undefined, this.model, this.capabilities);
  }
}

/**
 * Helper method to deal with Backend Browser Objects
 */
export function browserFromBackendObject(browser: any): Browser {
  let platform: Platform;
  if (!!(browser?.platform)) {
      let p = browser['platform'];
      platform = (typeof p === 'string') ? platformFromString(p) : platformFromObject(p);
  } else {
      platform = Platform.getUnknown();
  }

  return new Browser(browser['browserType'], browser['version'], platform, browser['properties'], browser['model'], browser['capabilities']);
}

/** helper function to compare stuff that might be a number */
export function compareMaybeNumeric(a: any, b: any, descending?: boolean): number{
  let desc = descending ?? false;
  if (a === undefined && b === undefined) {
      return 0;
  } else {
      if (isNumber(a) && isNumber(b)){
          return desc ? parseFloat(b) - parseFloat(a) : parseFloat(a) - parseFloat(b);
      } else {
          return desc ? b.toString().localeCompare(a.toString()) : a.toString().localeCompare(b.toString());
      }
  }
}

/** helper function to sort Browsers (for instance in Capabilities of Devices or Templates) */
export function browserSortingFunction(a: Browser, b: Browser): number {
  if (a.browserType === b.browserType) {
    let aplat = a.platform ? a.platform.getFullName() : undefined;
    let bplat = b.platform ? b.platform.getFullName() : undefined;
    if (!aplat || !bplat || aplat === bplat) {
      return compareMaybeNumeric(a.version, b.version);
    } else {
      return aplat.localeCompare(bplat);
    }
  } else {
    return a.browserType.localeCompare(b.browserType);
  }
}


